import i18n from '../../translations/i18n';
import { notUndefinedNull } from '../../helpers/shared';
import { handleRequest } from '../../helpers/request';
import { migrateSubmit, uploadFilesAndMigrateSubmit } from '../../services/tierApi';
import { setLoader, setDelay } from './shared';
import { setMessage } from './message';
import { getProductRequest } from './user';
import { endpoints } from '../../constants/endpoints';
import { chirpStatus, loanStatus } from '../../constants/form';
import { sendPostRequest, sendPutRequest } from '../../services/baseApi';
import { BASE_IBV } from '../types/base';

/**** Export functions  ****/
/**
 * Send migrat or update birfrost tier
 * @param {String} userIdBifrost
 * @param {Object} migratable
 * @param {String} userId
 * @param {String} productRequestId
 * @returns
 */
export const migrateBifrost = (
  userIdBifrost,
  migratable,
  userId,
  productRequestId
) => async dispatch => {
  try {
    let response = { error: false };

    if (migratable.edit) {
      response = await migrateSubmit(
        migratable.tier, userIdBifrost, migratable.migrate, productRequestId);
      dispatch(handleRequest(response, false));
      if (response) {
        dispatch(getProductRequest(userId));
      }
    }
    return response;
  } catch (error) {
    console.error('migrateBifrost: ', error);
    dispatch(setLoader(false));
    dispatch(setDelay(false));
    dispatch(setMessage(i18n.t("message.errorMigrate"), 'error'));
    return;
  }
};

/**
 * Upload document files and send migrate
 * @param {String} userIdBifrost
 * @param {Object} migratable
 * @param {String} userId
 * @param {String} productRequestId
 * @returns
 */
export const userFilesAndMigrateBifrost = (
  userIdBifrost,
  migratable,
  userId,
  productRequestId,
  files
) => async dispatch => {
  try {
    let response = { error: false };

    // Need to attach this data to process all the upload
    // files and migrate process
    files.append('bifrost_user_id', userIdBifrost);
    files.append('tier', migratable.tier.unique_name);
    files.append('product_request_id', productRequestId);

    // Need to update tier in Bifrost
    let payloadUpdate = {
      'bifrost_user_id': userIdBifrost.toString(),
      'tier': migratable.tier.unique_name,
      'product_request_id': productRequestId.toString()
    };
    if (localStorage.getItem('idRequest')){
      payloadUpdate.product_request_id = localStorage.getItem('idRequest');
    }
    payloadUpdate.new_verification_level = migratable.tier.verification_level;

    response = await uploadFilesAndMigrateSubmit(files, payloadUpdate);
    dispatch(handleRequest(response, false));
    if (response) {
      dispatch(getProductRequest(userId));
    }
    return response;
  } catch (error) {
    console.error('migrateBifrost: ', error);
    dispatch(setLoader(false));
    dispatch(setDelay(false));
    dispatch(setMessage(i18n.t("message.errorMigrate"), 'error'));
    return;
  }
};

/**
 * Updates the loan status for a user by sending a POST request to the customer IBV reports endpoint.
 * @async
 * @param {Object} params - Parameters for updating the loan.
 * @param {string} params.status - The new status of the loan.
 * @param {string} params.requestCode - The request code associated with the loan.
 * @param {string} params.bifrostUserId - The Bifrost user ID.
 * @returns {Promise<void>} Resolves when the request completes.
 */
export const updateLoan = async ({status, requestCode, bifrostUserId}) => {

  await sendPostRequest({
    'status': status,
    'request_code': requestCode,
    'bifrost_user_id': bifrostUserId
  }, endpoints.customerIBVReports);
}

/**
 * Validates the IBV (Instant Bank Verification) process for a user and dispatches related Redux actions.
 * Handles success and error paths, updates product status, and sends requests to endpoints as needed.
 * @async
 * @param {Object} data - Data required for IBV validation.
 * @param {Object} data.res - The response object containing IBV status details.
 * @param {Function} data.setAlert - Function to set alert state.
 * @param {string} [data.productId] - Optional product ID for updating product status.
 * @param {string} data.userId - The user's ID.
 * @param {string} data.statusId - The status ID to update.
 * @param {boolean} [data.completeCheck] - Optional flag to trigger complete check logic.
 * @param {string} data.chirpRequestCode - The request code for chirp IBV.
 * @param {string} data.userIdBifrost - The Bifrost user ID.
 * @param {Function} data.t - Translation function for error messages.
 * @returns {Promise<void>} Resolves when all async operations complete.
 */
export const validateIBV = (data) => async dispatch => {

  let success = data.res.request_status === chirpStatus.verified && (!data.res?.status_details || data.res?.status_details === 'NA')
  // 'default' success path
  if (success) {
    data.setAlert(true)
    dispatch({
      type: BASE_IBV,
      payload: {
        isIBVVerified: true,
        isIBVShown: false
      }
    })
    if (data.productId) {
      await sendPutRequest({
        "user": data.userId,
        "status": data.statusId,
        "validated_ibv": true
      }, endpoints.putProductRequest(data.productId))
    }
  } else {
    dispatch(setLoader(false));
    // setMessage('Needs to validate IBV', 'info')
    return;
  }

  // completeCheck path
  if (data.completeCheck && notUndefinedNull(data.completeCheck)) {
    // calling to 'customerIBVReports' on success
    if (success) {
      updateLoan({
        status: loanStatus?.verified, 
        requestCode: data.chirpRequestCode, 
        bifrostUserId: data.userIdBifrost
      })
    } else {
      // Error path
      let areStatusEqual = data.res?.request_status === data.res?.status_details
      let isBankSelected = data.res?.selected_bank !== 'NA'
      if (areStatusEqual && isBankSelected) {
        setMessage(data.t('ibv.invalidCredentials'), 'error') // Invalid credentials
      } else {
        setMessage(data.t('ibv.retry'), 'error') // General error
      }
    }
  }
}